import { Box, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { Icon } from '../Icon';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface CustomToastProps {
  isOpen: boolean;
  title: string;
  description: string;
  type?: ToastType;
  duration?: number;
  onClose: () => void;
  position?: {
    top?: string;
    right?: string;
    left?: string;
    bottom?: string;
  };
}

const getToastStyles = (type: ToastType) => {
  switch (type) {
    case 'success':
      return {
        borderColor: '#27AE60',
        iconBg: '#27AE60',
        iconName: 'check' as const,
        iconColor: 'white',
      };
    case 'error':
      return {
        borderColor: '#E74C3C',
        iconBg: '#E74C3C',
        iconName: 'close' as const,
        iconColor: 'white',
      };
    case 'warning':
      return {
        borderColor: '#F39C12',
        iconBg: '#F39C12',
        iconName: 'warning' as const,
        iconColor: 'white',
      };
    case 'info':
      return {
        borderColor: '#3498DB',
        iconBg: '#3498DB',
        iconName: 'info' as const,
        iconColor: 'white',
      };
    default:
      return {
        borderColor: '#27AE60',
        iconBg: '#27AE60',
        iconName: 'check' as const,
        iconColor: 'white',
      };
  }
};

export const CustomToast: React.FC<CustomToastProps> = ({
  isOpen,
  title,
  description,
  type = 'success',
  duration = 3000,
  onClose,
  position = { top: '112px', right: '24px' },
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);

      if (duration > 0) {
        const timer = setTimeout(() => {
          setIsVisible(false);
          setTimeout(onClose, 300); // Allow fade out animation
        }, duration);

        return () => clearTimeout(timer);
      }
    } else {
      setIsVisible(false);
    }
  }, [isOpen, duration, onClose]);

  if (!isOpen) {
    return null;
  }

  const styles = getToastStyles(type);

  return (
    <Box
      position="fixed"
      top={position.top}
      right={position.right}
      left={position.left}
      bottom={position.bottom}
      zIndex={9999}
      bg="surface.card"
      border={`1px solid ${styles.borderColor}`}
      borderRadius="12px"
      padding="16px"
      minWidth="320px"
      maxWidth="500px"
      boxShadow="0 4px 12px rgba(0, 0, 0, 0.1)"
      display="flex"
      gap={3}
      opacity={isVisible ? 1 : 0}
      transform={isVisible ? 'translateX(0)' : 'translateX(100%)'}
      transition="all 0.3s ease-in-out">
      <Box
        w="20px"
        h="20px"
        bg={styles.iconBg}
        borderRadius="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}>
        <Icon iconName={styles.iconName} boxSize={3} color={styles.iconColor} />
      </Box>
      <Box flex={1} minWidth={0}>
        <Text
          fontSize="14px"
          fontWeight="600"
          color="text.title"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis">
          {title}
        </Text>
        <Text fontSize="13px" fontWeight="400" color="text.muted" mt={1}>
          {description}
        </Text>
      </Box>
      <Box
        cursor="pointer"
        onClick={() => {
          setIsVisible(false);
          setTimeout(onClose, 300);
        }}
        color="text.title"
        fontSize="16px"
        fontWeight="bold"
        flexShrink={0}
        _hover={{ color: '#2C3E50' }}
        transition="color 0.2s ease">
        ×
      </Box>
    </Box>
  );
};
